"use client";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/Command";
import { Input } from "@/components/ui/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Check, ChevronDown, Globe2 } from "lucide-react";
import * as React from "react";
import * as RPNInput from "react-phone-number-input";
import { getCountryCallingCode, isValidPhoneNumber } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import cn from "twsx";

/* --------------------------------------------------------------------------------
   1) Create stable sub-components to avoid re-mounting on each render
----------------------------------------------------------------------------------*/

/** A stable custom input to pass into react-phone-number-input. */
const StableInput = ({ className, ref, ...props }: Omit<React.ComponentProps<"input">, "size"> & { ref?: React.Ref<HTMLInputElement> }) => {
  return (
    <Input
      ref={ref}
      className={cn("min-w-0 flex-1 rounded-s-none rounded-e-control", className)}
      {...props}
    />
  );
};
StableInput.displayName = "StableInput";

/** Flag component, made stable (defined outside the PhoneInput). */
const FlagComponent = React.memo(({
  country,
  countryName,
}: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-3.5 w-5 overflow-hidden rounded-xs bg-bg-secondary [&_svg]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
});
FlagComponent.displayName = "FlagComponent";

/** The country picker (popover + Command list). */
type CountryEntry = {
  label: string;
  value: RPNInput.Country | undefined;
  divider?: boolean;
};
type CountrySelectProps = {
  disabled?: boolean;
  iconComponent?: React.ElementType;
  readOnly?: boolean;
  value?: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country?: RPNInput.Country) => void;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "value">;

function getCallingCode(country?: RPNInput.Country) {
  return country ? `+${getCountryCallingCode(country)}` : "";
}

const CountrySelect = React.memo(({
  value: selectedCountry,
  options,
  disabled,
  readOnly,
  onChange,
  className,
  iconComponent: _iconComponent,
  ...buttonProps
}: CountrySelectProps) => {
  const [open, setOpen] = React.useState(false);
  const isDisabled = disabled || readOnly;
  const selectedOption = React.useMemo(
    () => options.find((option) => !option.divider && option.value === selectedCountry),
    [options, selectedCountry],
  );
  const selectedCallingCode = getCallingCode(selectedCountry);
  const selectedLabel = selectedOption?.label ?? selectedCountry ?? "International";

  const handleSelect = React.useCallback((country?: RPNInput.Country) => {
    onChange(country);
    setOpen(false);
  }, [onChange]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger
        {...buttonProps}
        disabled={isDisabled}
        role="combobox"
        aria-expanded={open}
        aria-label={buttonProps["aria-label"] ?? "Select phone country"}
        className={cn(
          "flex h-8 min-w-20 shrink-0 items-center justify-center gap-1.5 px-2 text-base font-medium text-text-secondary",
          "rounded-s-control border border-e-0 border-border-input bg-bg-control outline-none",
          "hover:bg-fill-hover hover:text-text-primary",
          "focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-accent",
          "disabled:cursor-not-allowed disabled:opacity-disabled",
          className,
        )}
      >
        {selectedCountry
          ? (
            <FlagComponent
              country={selectedCountry}
              countryName={selectedLabel}
            />
          )
          : <Globe2 className="size-3.5 text-text-tertiary" />}
        <span className="min-w-7 text-left">
          {selectedCallingCode || "Intl"}
        </span>
        <ChevronDown className="size-3 text-text-tertiary" />
      </PopoverTrigger>
      <PopoverContent width="md" padding="none" align="start">
        <Command>
          <CommandInput placeholder="Search country or code..." />
          <CommandList className="max-h-72">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {options.map((option, index) => {
                if (option.divider) {
                  return (
                    <div
                      key={`country-divider-${index}`}
                      role="separator"
                      className="my-1 h-px bg-border-primary"
                    />
                  );
                }

                const callingCode = getCallingCode(option.value);
                const isSelected = option.value === selectedCountry;
                const commandValue = [
                  option.label,
                  option.value,
                  callingCode,
                ].filter(Boolean).join(" ");

                return (
                  <CommandItem
                    key={option.value ?? "international"}
                    value={commandValue}
                    onSelect={() => handleSelect(option.value)}
                  >
                    {option.value
                      ? (
                        <FlagComponent
                          country={option.value}
                          countryName={option.label}
                        />
                      )
                      : <Globe2 className="size-3.5 text-text-tertiary" />}
                    <span className="flex min-w-0 grow items-center gap-2">
                      <span className="truncate">{option.label}</span>
                      {option.value && (
                        <span className="text-xs font-medium text-text-tertiary">
                          {option.value}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-sm font-medium text-text-tertiary">
                      {callingCode || "Intl"}
                    </span>
                    <Check
                      className={cn(
                        "size-3.5 text-accent",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
CountrySelect.displayName = "CountrySelect";

/* --------------------------------------------------------------------------------
   2) The main PhoneInput component
----------------------------------------------------------------------------------*/
type PhoneInputProps =
  & Omit<
    React.ComponentProps<"input">,
    "onChange" | "value" | "ref" | "size"
  >
  & Omit<RPNInput.Props<typeof RPNInput.default>, "onChange">
  & {
    onChange?: (value: RPNInput.Value | "") => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  };

/** Use when a person enters a phone number with a country code. */
export default function PhoneInput({ className, onChange, onBlur, value, disabled, ref, ...props }: PhoneInputProps & { ref?: React.Ref<React.ElementRef<typeof RPNInput.default>> }) {
  const [_isInvalid, setIsInvalid] = React.useState(false);

  const handleChange = React.useCallback(
    (val?: RPNInput.Value) => {
      onChange?.(val ?? "");
    },
    [onChange],
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (e.target.value) {
        setIsInvalid(!isValidPhoneNumber(e.target.value));
      }
      onBlur?.(e);
    },
    [onBlur],
  );

  return (
    <RPNInput.default
      ref={ref}
      className={cn("flex min-w-0", className)}
      smartCaret={false}
      // Provide stable sub-components
      flagComponent={FlagComponent as (props: RPNInput.FlagProps) => JSX.Element}
      countrySelectComponent={CountrySelect}
      inputComponent={StableInput}
      // Pass the necessary event handlers
      onChange={handleChange}
      onBlur={handleBlur}
      value={value}
      disabled={disabled}
      {...props}
    />
  );
}
PhoneInput.displayName = "PhoneInput";
